import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import { supabase } from '../lib/supabase'

const useLFSStore = create(
  devtools(
    (set, get) => ({
      // LFS Settings State
      lfsSettings: null,
      availabilitySlots: [],
      isLoadingSettings: false,
      settingsError: null,

      // Discovery State
      discoveryFilters: {
        regions: [],
        platforms: [],
        formats: [],
        search: ''
      },
      discoveredTeams: [],
      isLoadingTeams: false,
      teamsError: null,
      teamsPage: 1,
      hasMoreTeams: true,

      // Requests State
      incomingRequests: [],
      outgoingRequests: [],
      isLoadingRequests: false,
      requestsError: null,

      // Scrims State
      scheduledScrims: [],
      liveScrims: [],
      completedScrims: [],
      isLoadingScrims: false,
      scrimsError: null,
      scrimDetails: null,
      isLoadingScrim: false,
      scrimError: null,
      scrimsFilters: {
        status: 'all',
        dateFrom: null,
        dateTo: null
      },

      // Chat State
      chatMessages: {},
      isLoadingMessages: {},
      messagesError: null,
      chatError: null,
      currentUserId: null,

      // Computed State
      get scrims() {
        const state = get()
        return [
          ...state.scheduledScrims,
          ...state.liveScrims,
          ...state.completedScrims
        ]
      },

      // Computed state (will be updated via actions)
      unreadScrimRequestsCount: 0,
      unreadScrimChats: 0,
      scrimNotifications: {}, // { [scrimId]: { hasUnread: true, opponentTeam: 'name', lastMessageAt: timestamp } }
      unreadRequestNotifications: 0,
      requestNotifications: [], // Array of request notification objects
      unreadScrimCancellations: 0,
      scrimCancellationNotifications: [], // Array of scrim cancellation notification objects
      totalLFSNotifications: 0,

      // Helper function to update notification counts
      updateNotificationCounts: async () => {
        const state = get()
        
        // Count pending scrim requests (status should be 'Proposed')
        const pendingRequests = state.incomingRequests.filter(req => 
          req.status === 'Proposed'
        )
        
        // Count unread scrim chats
        const unreadChats = await get().getUnreadScrimChats()
        
        // Count unread request notifications (accepted/declined/cancelled requests)
        const requestNotifications = await get().getUnreadRequestNotifications()
        
        // Count unread scrim cancellation notifications
        const scrimCancellationNotifications = await get().getUnreadScrimNotifications()
        
        const newCounts = {
          unreadScrimRequestsCount: pendingRequests.length,
          unreadScrimChats: unreadChats.length,
          scrimNotifications: unreadChats.reduce((acc, chat) => ({
            ...acc,
            [chat.scrimId]: {
              hasUnread: true,
              opponentTeam: chat.opponentTeamName,
              lastMessageAt: chat.lastMessageAt
            }
          }), {}),
          unreadRequestNotifications: requestNotifications.length,
          requestNotifications: requestNotifications,
          unreadScrimCancellations: scrimCancellationNotifications.length,
          scrimCancellationNotifications: scrimCancellationNotifications,
          totalLFSNotifications: pendingRequests.length + unreadChats.length + requestNotifications.length + scrimCancellationNotifications.length
        }
        
        set(newCounts)
      },

      // Get scrims that have unread messages for the user
      getUnreadScrimChats: async () => {
        try {
          const { data: { user } } = await supabase.auth.getUser()
          if (!user) return []

          // Get user's teams
          const { data: userTeams } = await supabase
            .from('team_members')
            .select('team_id')
            .eq('user_id', user.id)

          const teamIds = userTeams?.map(t => t.team_id) || []
          if (teamIds.length === 0) return []

          // First get scrims involving user's teams
          const { data: userScrims, error: scrimsError } = await supabase
            .from('scrims')
            .select('id')
            .or(teamIds.map(id => `team_a_id.eq.${id},team_b_id.eq.${id}`).join(','))

          if (scrimsError) {
            console.error('Error fetching user scrims:', scrimsError)
            return []
          }

          if (!userScrims || userScrims.length === 0) return []

          const scrimIds = userScrims.map(s => s.id)

          // Get unread messages from those scrims
          const { data, error } = await supabase
            .from('scrim_messages')
            .select(`
              scrim_id,
              created_at,
              scrims!inner(
                id,
                team_a_id, 
                team_b_id,
                team_a:team_a_id(name),
                team_b:team_b_id(name)
              )
            `)
            .in('scrim_id', scrimIds)
            .not('sender_user_id', 'eq', user.id)
            .not('read_by', 'cs', `{${user.id}}`) // Not in read_by array (cs = contains)

          if (error) {
            console.error('Error fetching unread scrim chats:', error)
            return []
          }

          if (!data || data.length === 0) return []

          // Group by scrim and get the latest message per scrim
          const scrimChats = {}
          
          data.forEach(message => {
            const scrimId = message.scrim_id
            const scrim = message.scrims
            
            // Determine opponent team name
            const userTeamId = teamIds.find(id => id === scrim.team_a_id || id === scrim.team_b_id)
            const opponentTeamName = scrim.team_a_id === userTeamId ? scrim.team_b.name : scrim.team_a.name
            
            // Keep only the latest message per scrim
            if (!scrimChats[scrimId] || new Date(message.created_at) > new Date(scrimChats[scrimId].lastMessageAt)) {
              scrimChats[scrimId] = {
                scrimId,
                opponentTeamName,
                lastMessageAt: message.created_at
              }
            }
          })

          const result = Object.values(scrimChats)
          return result
        } catch (error) {
          console.error('Error getting unread scrim chats:', error)
          return []
        }
      },

      // Get unread request notifications (accepted/declined/cancelled requests)
      getUnreadRequestNotifications: async () => {
        try {
          const { data: { user } } = await supabase.auth.getUser()
          if (!user) return []

          // Get user's teams
          const { data: userTeams } = await supabase
            .from('team_members')
            .select('team_id')
            .eq('user_id', user.id)

          const teamIds = userTeams?.map(t => t.team_id) || []
          if (teamIds.length === 0) return []

          // Get unread request notifications using the database function
          const { data, error } = await supabase.rpc('get_unread_request_notifications', {
            p_team_ids: teamIds
          })

          if (error) {
            console.error('Error fetching unread request notifications:', error)
            return []
          }

          return data || []
        } catch (error) {
          console.error('Error getting unread request notifications:', error)
          return []
        }
      },

      // Mark request notification as read
      markRequestNotificationRead: async (requestId, teamRole) => {
        try {
          const functionName = teamRole === 'from_team' ? 'mark_from_team_notification_read' : 'mark_to_team_notification_read'

          const { error } = await supabase.rpc(functionName, {
            p_request_id: requestId
          })

          if (error) {
            console.error('Error marking notification as read:', error)
            return { success: false, error }
          }

          // Update notification counts
          get().updateNotificationCounts()
          
          return { success: true }
        } catch (error) {
          console.error('Error marking notification as read:', error)
          return { success: false, error }
        }
      },

      // Handle new message notification logic
      handleNewMessageNotification: async (scrimId, message) => {
        try {
          const { data: { user } } = await supabase.auth.getUser()
          if (!user) return
          
          // Only create notification if message is not from current user
          if (message.sender_user_id === user.id) {
            return
          }

          // Check if user is part of this scrim
          const { data: userTeams } = await supabase
            .from('team_members')
            .select('team_id')
            .eq('user_id', user.id)

          const teamIds = userTeams?.map(t => t.team_id) || []
          if (teamIds.length === 0) return

          // Get scrim details to verify user is involved
          const { data: scrim } = await supabase
            .from('scrims')
            .select(`
              id,
              team_a_id, 
              team_b_id,
              team_a:team_a_id(name),
              team_b:team_b_id(name)
            `)
            .eq('id', scrimId)
            .single()

          if (!scrim) return

          const userTeamId = teamIds.find(id => id === scrim.team_a_id || id === scrim.team_b_id)
          if (!userTeamId) {
            return
          }

          // Determine opponent team name
          const opponentTeamName = scrim.team_a_id === userTeamId ? scrim.team_b.name : scrim.team_a.name

          const state = get()
          
          // Check if this scrim already has unread notifications
          if (state.scrimNotifications[scrimId]?.hasUnread) {
            return
          }

          // This is a NEW chat notification - first unread message in this scrim
          
          const newScrimNotifications = {
            ...state.scrimNotifications,
            [scrimId]: {
              hasUnread: true,
              opponentTeam: opponentTeamName,
              lastMessageAt: message.created_at
            }
          }
          
          const unreadCount = Object.keys(newScrimNotifications).length
          
          set({
            scrimNotifications: newScrimNotifications,
            unreadScrimChats: unreadCount,
            totalLFSNotifications: state.unreadScrimRequestsCount + unreadCount
          })

        } catch (error) {
          console.error('Error handling new message notification:', error)
        }
      },

      // Handle when messages are marked as read via realtime
      handleMessageReadUpdate: async (scrimId) => {
        
        // Check if this scrim still has unread messages for current user
        const unreadChats = await get().getUnreadScrimChats()
        const stillHasUnread = unreadChats.some(chat => chat.scrimId === scrimId)
        
        const state = get()
        const newScrimNotifications = { ...state.scrimNotifications }
        
        if (!stillHasUnread && newScrimNotifications[scrimId]) {
          // Remove notification for this scrim
          delete newScrimNotifications[scrimId]
          
          const unreadCount = Object.keys(newScrimNotifications).length
          
          set({
            scrimNotifications: newScrimNotifications,
            unreadScrimChats: unreadCount,
            totalLFSNotifications: state.unreadScrimRequestsCount + unreadCount
          })
          
        }
      },

      // Actions
      loadLFSSettings: async (teamId) => {
        set({ isLoadingSettings: true, settingsError: null })
        
        try {
          // Load LFS settings
          const { data: lfsData, error: lfsError } = await supabase
            .from('team_lfs_settings')
            .select('*')
            .eq('team_id', teamId)
            .maybeSingle()

          if (lfsError && lfsError.code !== 'PGRST116') {
            throw lfsError
          }

          // Load availability slots
          const { data: slotsData, error: slotsError } = await supabase
            .from('team_availability_slots')
            .select('*')
            .eq('team_id', teamId)
            .order('weekday, start_time')

          if (slotsError) throw slotsError

          set({
            lfsSettings: lfsData || {
              team_id: teamId,
              lfs_enabled: false,
              regions: [],
              platforms: [],
              formats: []
            },
            availabilitySlots: slotsData || [],
            isLoadingSettings: false
          })
          return { lfsSettings: lfsData, availabilitySlots: slotsData }
        } catch (error) {
          console.error('Error loading LFS settings:', error)
          set({ settingsError: error.message, isLoadingSettings: false })
        }
      },

      saveLFSSettings: async (teamId, settings) => {
        set({ isLoadingSettings: true, settingsError: null })
        
        try {
          const { data, error } = await supabase
            .from('team_lfs_settings')
            .upsert({
              team_id: teamId,
              ...settings,
              updated_at: new Date().toISOString()
            })
            .select()
            .single()

          if (error) throw error

          set({ lfsSettings: data, isLoadingSettings: false })
          return { success: true }
        } catch (error) {
          console.error('Error saving LFS settings:', error)
          set({ settingsError: error.message, isLoadingSettings: false })
          return { success: false, error: error.message }
        }
      },

      saveAvailabilitySlots: async (teamId, slots) => {
        try {
          // Delete existing slots
          await supabase
            .from('team_availability_slots')
            .delete()
            .eq('team_id', teamId)

          // Insert new slots (exclude id field to let database auto-generate)
          if (slots.length > 0) {
            const { data, error } = await supabase
              .from('team_availability_slots')
              .insert(slots.map(slot => {
                const { id, ...slotWithoutId } = slot
                return { 
                  ...slotWithoutId, 
                  team_id: teamId,
                  created_at: new Date().toISOString()
                }
              }))
              .select()

            if (error) throw error
            set({ availabilitySlots: data })
          } else {
            set({ availabilitySlots: [] })
          }

          return { success: true }
        } catch (error) {
          console.error('Error saving availability slots:', error)
          return { success: false, error: error.message }
        }
      },

      // Get unread scrim cancellation notifications
      getUnreadScrimNotifications: async () => {
        try {
          const { data: { user } } = await supabase.auth.getUser()
          if (!user) return []

          // Get user's teams
          const { data: userTeams } = await supabase
            .from('team_members')
            .select('team_id')
            .eq('user_id', user.id)

          const teamIds = userTeams?.map(t => t.team_id) || []
          if (teamIds.length === 0) return []

          // Get unread scrim cancellation notifications using the database function
          const { data, error } = await supabase.rpc('get_unread_scrim_notifications', {
            p_team_ids: teamIds
          })

          if (error) {
            console.error('Error fetching unread scrim notifications:', error)
            return []
          }

          return data || []
        } catch (error) {
          console.error('Error getting unread scrim notifications:', error)
          return []
        }
      },

      // Mark scrim notification as read
      markScrimNotificationRead: async (scrimId, userTeamIds) => {
        try {
          // Get the scrim to determine which team the user is on
          const { data: scrim, error: scrimError } = await supabase
            .from('scrims')
            .select('team_a_id, team_b_id')
            .eq('id', scrimId)
            .single()

          if (scrimError || !scrim) {
            console.error('Error fetching scrim for notification:', scrimError)
            return { success: false, error: scrimError }
          }

          // Determine which team the user is on
          const isTeamA = userTeamIds.includes(scrim.team_a_id)
          const functionName = isTeamA ? 'mark_scrim_team_a_notification_read' : 'mark_scrim_team_b_notification_read'

          const { error } = await supabase.rpc(functionName, {
            p_scrim_id: scrimId
          })

          if (error) {
            console.error(`Error marking scrim notification as read:`, error)
            return { success: false, error }
          }

          // Update notification counts
          get().updateNotificationCounts()
          
          return { success: true }
        } catch (error) {
          console.error('Error marking scrim notification as read:', error)
          return { success: false, error }
        }
      },

      setDiscoveryFilters: (filters) => {
        set({ 
          discoveryFilters: { ...get().discoveryFilters, ...filters },
          teamsPage: 1,
          discoveredTeams: [],
          hasMoreTeams: true
        })
      },

      searchTeams: async (reset = false) => {
        const state = get()
        
        if (!reset && (!state.hasMoreTeams || state.isLoadingTeams)) return

        set({ isLoadingTeams: true, teamsError: null })
        
        try {
          // Test direct database query first
          const { error: testError } = await supabase
            .from('team_lfs_settings')
            .select(`
              team_id,
              lfs_enabled,
              regions,
              platforms,
              formats,
              updated_at,
              teams:team_id (
                id,
                name,
                logo,
                country_code
              )
            `)
            .eq('lfs_enabled', true)
            .limit(5)
          
          
          if (testError) {
            console.error('Direct query failed:', testError)
            set({ 
              teamsError: `Database error: ${testError.message}`,
              isLoadingTeams: false 
            })
            return
          }
          
          // If direct query works, try Edge Function
          const { data, error } = await supabase.functions.invoke('lfs-search-teams', {
            body: {
              ...state.discoveryFilters,
              page: reset ? 1 : state.teamsPage,
              limit: 20
            }
          })

          if (error) {
            console.error('Edge Function error details:', error)
            throw error
          }

          const newTeams = (data.teams || []).filter(team => team.teams?.is_active === true)
          
          set({
            discoveredTeams: reset ? newTeams : [...state.discoveredTeams, ...newTeams],
            teamsPage: reset ? 2 : state.teamsPage + 1,
            hasMoreTeams: data.pagination.hasMore,
            isLoadingTeams: false
          })
        } catch (error) {
          console.error('Full error object:', error)
          console.error('Error searching teams:', error)
          
          // Try to get more details from the response
          let errorMessage = error.message
          if (error.context?.body) {
            try {
              const errorBody = await error.context.body.text()
              console.error('Error response body:', errorBody)
              errorMessage += ` - Response: ${errorBody}`
            } catch (e) {
              console.error('Could not read error body:', e)
            }
          }
          
          set({ teamsError: errorMessage, isLoadingTeams: false })
        }
      },

      challengeTeam: async (challengeData) => {
        try {
          const { data, error } = await supabase.functions.invoke('lfs-challenge', {
            body: challengeData
          })

          if (error) throw error

          // Refresh outgoing requests
          get().loadRequests()
          
          return { success: true, request: data.request }
        } catch (error) {
          console.error('Error challenging team:', error)
          return { success: false, error: error.message }
        }
      },

      loadRequests: async () => {
        set({ isLoadingRequests: true, requestsError: null })
        
        try {
          const { data: { user } } = await supabase.auth.getUser()
          if (!user) throw new Error('Not authenticated')

          // Get user's teams
          const { data: userTeams } = await supabase
            .from('team_members')
            .select('team_id')
            .eq('user_id', user.id)

          const teamIds = userTeams?.map(t => t.team_id) || []

          if (teamIds.length === 0) {
            set({ 
              incomingRequests: [], 
              outgoingRequests: [], 
              isLoadingRequests: false 
            })
            return
          }

          // Load incoming requests
          const { data: incoming, error: incomingError } = await supabase
            .from('scrim_requests')
            .select(`
              *,
              from_team:from_team_id (id, name, logo, country_code),
              to_team:to_team_id (id, name, logo, country_code)
            `)
            .in('to_team_id', teamIds)
            .not('status', 'in', '(Expired,Cancelled,Accepted,Countered_Closed)')
            .order('created_at', { ascending: false })

          if (incomingError) throw incomingError

          // Load outgoing requests
          const { data: outgoing, error: outgoingError } = await supabase
            .from('scrim_requests')
            .select(`
              *,
              from_team:from_team_id (id, name, logo, country_code),
              to_team:to_team_id (id, name, logo, country_code)
            `)
            .in('from_team_id', teamIds)
            .not('status', 'in', '(Expired,Cancelled,Accepted,Countered_Closed)')
            .order('created_at', { ascending: false })

          if (outgoingError) throw outgoingError

          set({
            incomingRequests: incoming || [],
            outgoingRequests: outgoing || [],
            isLoadingRequests: false
          })
          
          // Update notification counts after loading requests
          await get().updateNotificationCounts()
        } catch (error) {
          console.error('Error loading requests:', error)
          set({ requestsError: error.message, isLoadingRequests: false })
        }
      },

      respondToRequest: async (requestId, action, data = {}) => {
        try {
          if (action === 'counter') {
            // Step 1: Update the original request's status to 'Countered_Closed'
            // This requires a backend function to update the status of the existing request.
            // The 'lfs-respond' function should be extended to handle this specific action.
            const { error: updateError } = await supabase.functions.invoke('lfs-respond', {
              body: {
                request_id: requestId,
                action: 'counter_close_original', // New action to close the original request
                notes: data.notes // Pass notes if any
              }
            })

            if (updateError) throw updateError

            // Small delay to ensure database consistency before creating the new challenge
            await new Promise(resolve => setTimeout(resolve, 500))

            // Step 2: Create a new challenge from the receiver to the original sender
            // Get the original request data directly from database since it may have been filtered out after status change
            const { data: originalRequestFromDB, error: fetchError } = await supabase
              .from('scrim_requests')
              .select('*')
              .eq('id', requestId)
              .single()

            if (fetchError || !originalRequestFromDB) {
              throw new Error('Original request not found for counter-proposal.')
            }

            const originalRequest = originalRequestFromDB

            // Counter proposal should use the EXACT same settings as the original request
            // If the original request was valid, the counter should be too

            const newChallengeData = {
              from_team_id: String(originalRequest.to_team_id), // Receiver becomes sender (ensure string)
              to_team_id: String(originalRequest.from_team_id),   // Sender becomes receiver (ensure string)
              region: originalRequest.region,
              platform: originalRequest.platform,
              format: originalRequest.format,
              proposed_slots: data.counter_slots || [],
              notes: data.notes || ''
            }


            const { data: newChallengeResponse, error: challengeError } = await supabase.functions.invoke('lfs-challenge', {
              body: newChallengeData
            })

            if (challengeError) throw challengeError

            // Refresh requests and scrims
            get().loadRequests()
            get().loadScrims()

            return { success: true, request: newChallengeResponse.request }

          } else {
            // Existing logic for accept and decline actions
            const { data: response, error } = await supabase.functions.invoke('lfs-respond', {
              body: {
                request_id: requestId,
                action,
                ...data
              }
            })

            if (error) throw error

            // Refresh requests and scrims
            get().loadRequests()
            get().loadScrims()

            return { success: true, ...response }
          }
        } catch (error) {
          console.error('Error responding to request:', error)
          return { success: false, error: error.message }
        }
      },

      loadScrims: async () => {
        set({ isLoadingScrims: true, scrimsError: null })
        
        try {
          const { data: { user } } = await supabase.auth.getUser()
          if (!user) throw new Error('Not authenticated')

          // Get user's teams
          const { data: userTeams } = await supabase
            .from('team_members')
            .select('team_id')
            .eq('user_id', user.id)

          const teamIds = userTeams?.map(t => t.team_id) || []

          if (teamIds.length === 0) {
            set({ 
              scheduledScrims: [], 
              liveScrims: [], 
              completedScrims: [], 
              isLoadingScrims: false 
            })
            return
          }

          // Build query
          let query = supabase
            .from('scrims')
            .select(`
              *,
              team_a:team_a_id (id, name, logo, country_code),
              team_b:team_b_id (id, name, logo, country_code),
              winner:winner_team_id (id, name, logo)
            `)
            .or(teamIds.map(id => `team_a_id.eq.${id},team_b_id.eq.${id}`).join(','))
            .order('scheduled_at', { ascending: false })

          const { data: scrims, error } = await query

          if (error) throw error


          // Categorize scrims
          const scheduled = scrims?.filter(s => s.status === 'Scheduled') || []
          const live = scrims?.filter(s => s.status === 'Live') || []
          const completed = scrims?.filter(s => s.status === 'Completed') || []

          set(state => ({
            ...state,
            scheduledScrims: scheduled,
            liveScrims: live,
            completedScrims: completed,
            isLoadingScrims: false
          }))

        } catch (error) {
          console.error('Error loading scrims:', error)
          set({ scrimsError: error.message, isLoadingScrims: false })
        }
      },

      performScrimAction: async (scrimId, action, data = {}) => {
        try {
          const { data: response, error } = await supabase.functions.invoke('lfs-scrim-actions', {
            body: {
              scrim_id: scrimId,
              action,
              ...data
            }
          })

          if (error) throw error

          // Refresh scrims with a slight delay to ensure database update propagates
          setTimeout(() => {
            get().loadScrims()
          }, 500) // 500ms delay

          return { success: true, scrim: response.scrim }
        } catch (error) {
          console.error(`Error in performScrimAction for ${action} on ${scrimId}:`, error)
          return { success: false, error: error.message }
        }
      },

      markScrimCompleted: async (scrimId, team_a_score, team_b_score, winner_team_id) => {
        return get().performScrimAction(scrimId, 'complete', { team_a_score, team_b_score, winner_team_id })
      },

      cancelScrim: async (scrimId) => {
        return get().performScrimAction(scrimId, 'cancel')
      },

      loadScrimDetails: async (scrimId) => {
        set({ isLoadingScrim: true, scrimError: null })
        
        try {
          const { data: scrim, error } = await supabase
            .from('scrims')
            .select(`
              *,
              team_a:team_a_id (id, name, logo),
              team_b:team_b_id (id, name, logo)
            `)
            .eq('id', scrimId)
            .single()

          if (error) throw error

          set({ 
            scrimDetails: scrim,
            isLoadingScrim: false 
          })
        } catch (error) {
          console.error('Error loading scrim details:', error)
          set({ 
            scrimError: error.message, 
            isLoadingScrim: false 
          })
        }
      },

      loadChatMessages: async (scrimId) => {
        set({ 
          isLoadingMessages: { ...get().isLoadingMessages, [scrimId]: true },
          messagesError: null 
        })
        
        try {
          const { data: messages, error } = await supabase
            .from('scrim_messages')
            .select(`
              *,
              sender_team:sender_team_id (id, name, logo)
            `)
            .eq('scrim_id', scrimId)
            .order('created_at', { ascending: true })

          if (error) throw error

          set({
            chatMessages: { ...get().chatMessages, [scrimId]: messages || [] },
            isLoadingMessages: { ...get().isLoadingMessages, [scrimId]: false }
          })
          
        } catch (error) {
          console.error('Error loading chat messages:', error)
          set({ 
            messagesError: error.message,
            isLoadingMessages: { ...get().isLoadingMessages, [scrimId]: false }
          })
        }
      },

      sendChatMessage: async (scrimId, content, senderTeamId) => {
        try {
          const { data: { user } } = await supabase.auth.getUser()
          if (!user) throw new Error('Not authenticated')

          const { data, error } = await supabase
            .from('scrim_messages')
            .insert({
              scrim_id: scrimId,
              sender_team_id: senderTeamId,
              sender_user_id: user.id,
              content
            })
            .select(`
              *,
              sender_team:sender_team_id (id, name, logo)
            `)
            .single()

          if (error) throw error

          // Add message to local state
          const currentMessages = get().chatMessages[scrimId] || []
          set({
            chatMessages: {
              ...get().chatMessages,
              [scrimId]: [...currentMessages, data]
            }
          })

          return { success: true }
        } catch (error) {
          console.error('Error sending chat message:', error)
          return { success: false, error: error.message }
        }
      },

      // Realtime subscriptions
      subscribeToRequests: (teamIds) => {
        const channel = supabase
          .channel('lfs_requests')
          .on(
            'postgres_changes',
            {
              event: '*',
              schema: 'public',
              table: 'scrim_requests',
              filter: `to_team_id.in.(${teamIds.join(',')})` 
            },
            async (payload) => {
              // Use setTimeout to avoid setState during render
              setTimeout(async () => {
                await get().loadRequests()
                // Update notification counts for any changes (includes new notifications)
                await get().updateNotificationCounts()
              }, 0)
            }
          )
          .on(
            'postgres_changes',
            {
              event: '*',
              schema: 'public',
              table: 'scrim_requests',
              filter: `from_team_id.in.(${teamIds.join(',')})` 
            },
            async (payload) => {
              // Use setTimeout to avoid setState during render
              setTimeout(async () => {
                await get().loadRequests()
                // Update notification counts for any changes (includes new notifications)
                await get().updateNotificationCounts()
              }, 0)
            }
          )
          .subscribe()

        return channel
      },

      subscribeToScrims: (teamIds) => {
        const channel = supabase
          .channel('lfs_scrims')
          .on(
            'postgres_changes',
            {
              event: '*',
              schema: 'public',
              table: 'scrims',
              filter: `team_a_id.in.(${teamIds.join(',')})` 
            },
            async (payload) => {
              get().loadScrims()
              
              // If a scrim was cancelled, trigger notification counts update
              if (payload.eventType === 'UPDATE' && payload.new?.status === 'Cancelled') {
                setTimeout(async () => {
                  await get().updateNotificationCounts()
                }, 100)
              }
            }
          )
          .on(
            'postgres_changes',
            {
              event: '*',
              schema: 'public',
              table: 'scrims',
              filter: `team_b_id.in.(${teamIds.join(',')})` 
            },
            async (payload) => {
              get().loadScrims()
              
              // If a scrim was cancelled, trigger notification counts update
              if (payload.eventType === 'UPDATE' && payload.new?.status === 'Cancelled') {
                setTimeout(async () => {
                  await get().updateNotificationCounts()
                }, 100)
              }
            }
          )
          .subscribe()

        return channel
      },

      subscribeToChat: (scrimId) => {
        const channel = supabase
          .channel(`scrim_chat_${scrimId}`)
          .on(
            'postgres_changes',
            {
              event: 'INSERT',
              schema: 'public',
              table: 'scrim_messages',
              filter: `scrim_id=eq.${scrimId}`
            },
            async (payload) => {
              
              const newMessage = payload.new
              const state = get()
              
              // Load the full message with relations for UI
              const { data: message } = await supabase
                .from('scrim_messages')
                .select(`
                  *,
                  sender_team:sender_team_id (id, name, logo)
                `)
                .eq('id', newMessage.id)
                .single()

              if (message) {
                const currentMessages = state.chatMessages[scrimId] || []
                
                // Check if message already exists to prevent duplicates
                const messageExists = currentMessages.some(m => m.id === message.id)
                
                if (!messageExists) {
                  set({
                    chatMessages: {
                      ...get().chatMessages,
                      [scrimId]: [...currentMessages, message]
                    }
                  })
                  
                  // Handle chat-level notification
                  await get().handleNewMessageNotification(scrimId, newMessage)
                } else {
                }
              }
            }
          )
          .on(
            'postgres_changes',
            {
              event: 'UPDATE',
              schema: 'public',
              table: 'scrim_messages',
              filter: `scrim_id=eq.${scrimId}`
            },
            async (payload) => {
              
              // Handle when messages are marked as read
              await get().handleMessageReadUpdate(scrimId)
            }
          )
          .subscribe((status) => {
          })

        return channel
      },

      subscribeToScrimChat: (scrimId) => {
        return get().subscribeToChat(scrimId)
      },


      // Notification management
      setCurrentUser: (userId) => {
        set({ currentUserId: userId })
        // Update notification counts after setting current user
        setTimeout(() => get().updateNotificationCounts(), 100) // Use timeout to avoid blocking
      },

      markScrimChatAsRead: async (scrimId) => {
        try {
          const { data: { user } } = await supabase.auth.getUser()
          if (!user) throw new Error('Not authenticated')


          // Bulk update: add user to read_by array for all unread messages in this scrim
          const { error } = await supabase
            .rpc('mark_scrim_messages_as_read', {
              p_scrim_id: scrimId,
              p_user_id: user.id
            })

          if (error) {
            console.error('Error marking scrim chat as read:', error)
            // Fallback to manual update if RPC doesn't exist
            const { error: updateError } = await supabase
              .from('scrim_messages')
              .update({
                read_by: supabase.raw(`array_append(read_by, '${user.id}'::uuid)`)
              })
              .eq('scrim_id', scrimId)
              .neq('sender_user_id', user.id)
              .not('read_by', 'cs', `{${user.id}}`)

            if (updateError) {
              console.error('Error with fallback update:', updateError)
              return { success: false, error: updateError.message }
            }
          }

          // Update local state immediately - remove this scrim from notifications
          const state = get()
          const newScrimNotifications = { ...state.scrimNotifications }
          delete newScrimNotifications[scrimId]
          
          const unreadCount = Object.keys(newScrimNotifications).length
          
          set({
            scrimNotifications: newScrimNotifications,
            unreadScrimChats: unreadCount,
            totalLFSNotifications: state.unreadScrimRequestsCount + unreadCount
          })

          return { success: true }
        } catch (error) {
          console.error('Error marking scrim chat as read:', error)
          return { success: false, error: error.message }
        }
      },

      markScrimRequestAsRead: (requestId) => {
        const state = get()
        set({
          incomingRequests: state.incomingRequests.map(req =>
            req.id === requestId ? { ...req, read_at: new Date().toISOString() } : req
          )
        })
      },

      // Reset functions
      resetDiscovery: () => {
        set({
          discoveredTeams: [],
          teamsPage: 1,
          hasMoreTeams: true,
          teamsError: null
        })
      },

      resetStore: () => {
        set({
          lfsSettings: null,
          availabilitySlots: [],
          discoveredTeams: [],
          incomingRequests: [],
          outgoingRequests: [],
          scheduledScrims: [],
          liveScrims: [],
          completedScrims: [],
          chatMessages: {},
          isLoadingMessages: {},
          unreadScrimRequestsCount: 0,
          unreadScrimChats: 0,
          scrimNotifications: {},
          unreadRequestNotifications: 0,
          requestNotifications: [],
          totalLFSNotifications: 0
        })
      }
    }),
    {
      name: 'lfs-store'
    }
  )
)

export default useLFSStore